// Elegir a mano el asiento del mayor con el que se concilia un consumo.
//
// Replica pickerHTML() del original: lista los asientos candidatos (CARGO y
// AJUSTE), con buscador por proveedor, sigla o importe, y muestra el estado de
// cada uno — libre, ya usado por otro consumo, o el N° con el que se concilió.
//
// Al confirmar, la API calcula el estado y la diferencia, y APRENDE el alias
// para los próximos resúmenes.
import { useMemo, useState } from 'react';
import type { Movimiento, Vinculo } from '../tipos';

const pesos = (v: string | number) =>
  Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  consumo: { id: string; comercio: string; importe: string; moneda: string; fecha: string };
  movimientos: Movimiento[];
  vinculos: Vinculo[];
  vinculoActual: Vinculo | undefined;
  onElegir: (movimientoId: string | null) => void;
  onCancelar: () => void;
  guardando: boolean;
}

export function SelectorAsiento({
  consumo, movimientos, vinculos, vinculoActual, onElegir, onCancelar, guardando,
}: Props) {
  const [busqueda, setBusqueda] = useState('');

  // Solo cargos y ajustes son candidatos: un pago del resumen no concilia
  // contra un consumo. Mismo criterio que el original.
  const candidatos = useMemo(
    () => movimientos.filter((m) => m.clase === 'CARGO' || m.clase === 'AJUSTE'),
    [movimientos],
  );

  const usadoPor = useMemo(() => {
    const m = new Map<string, Vinculo>();
    for (const v of vinculos) if (v.movimientoId) m.set(v.movimientoId, v);
    return m;
  }, [vinculos]);

  const q = busqueda.toLowerCase().trim();
  const visibles = candidatos.filter((m) => {
    if (!q) return true;
    const txt = `${m.proveedor ?? ''} ${m.concepto ?? ''} ${m.importe} ${Math.round(Math.abs(Number(m.importe)))} ${m.fecha}`.toLowerCase();
    return txt.includes(q);
  });

  const impConsumo = Number(consumo.importe);

  // Va como diálogo centrado y no como panel flotante junto a la celda: el
  // contenedor de la tabla tiene overflow:auto, que RECORTA a cualquier hijo
  // posicionado. Flotando quedaba inusable.
  return (
    <div className="selector-fondo" onClick={onCancelar}>
    <div className="selector" role="dialog" aria-label="Elegir asiento del mayor"
         onClick={(e) => e.stopPropagation()}>
      <div className="selector-cab">
        <input type="text" value={busqueda} autoFocus
               placeholder="Buscar proveedor, sigla o importe…"
               onChange={(e) => setBusqueda(e.target.value)} />
        <button className="btn-ghost" onClick={onCancelar} title="Cerrar sin cambiar">✕</button>
      </div>

      <div className="selector-lista">
        <button className="selector-opt sin" disabled={guardando}
                onClick={() => onElegir(null)}>
          — sin vínculo (queda no conciliado) —
        </button>

        {visibles.length === 0 && (
          <div className="selector-opt muted">No hay asientos que coincidan con «{busqueda}».</div>
        )}

        {visibles.map((m) => {
          const usado = usadoPor.get(m.id);
          const esteMismo = usado && usado.consumoId === consumo.id;
          const dif = Math.abs(Math.abs(Number(m.importe)) - impConsumo);
          return (
            <button key={m.id} disabled={guardando}
                    className={`selector-opt${esteMismo ? ' elegida' : ''}`}
                    onClick={() => onElegir(m.id)}>
              <span className="selector-fecha">{m.fecha.slice(5)}</span>
              <span className="selector-prov">{m.proveedor ?? m.concepto ?? '(sin descripción)'}</span>
              <span className="selector-imp">{pesos(m.importe)}</span>
              {usado
                ? <span className={`tag ${esteMismo ? 'ok' : 'warn'}`}>
                    {esteMismo ? `este · ${usado.nroTilde}` : `usado · ${usado.nroTilde}`}
                  </span>
                : <span className="tag info">libre</span>}
              {consumo.moneda !== 'USD' && (
                <span className={`selector-dif ${dif < 0.5 ? 'exacto' : ''}`}>
                  {dif < 0.5 ? 'cuadra exacto' : `dif ${pesos(dif)}`}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="selector-pie muted">
        Conciliando <b>{consumo.comercio}</b> del {consumo.fecha} ·{' '}
        {pesos(consumo.importe)} {consumo.moneda}
        {vinculoActual?.movimientoId && ' · ya tiene un asiento vinculado'}
      </div>
    </div>
    </div>
  );
}
